import Icon from 'components/Icon';
import {format, isValid, parse} from 'date-fns';
import {
    ChangeEvent,
    forwardRef,
    HTMLAttributes,
    MouseEvent,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {twMerge} from 'tailwind-merge';
import {c} from 'ttag';

import DateSelectContext from '../context';
import {checkValidTime, isWithinRange} from '../utils';
import {dateSelectInput} from './styles.module.css';

type DateSelectInputProps = {
    className?: string;
    isCompact?: boolean;
    placeholder?: string;
} & Omit<
    HTMLAttributes<HTMLInputElement>,
    'disabled' | 'onBlur' | 'onChange' | 'value'
>;

const DateSelectInput = forwardRef<HTMLInputElement, DateSelectInputProps>(
    (props, ref) => {
        const {className, isCompact, placeholder, ...rest} = props;

        const {
            disabled,
            error,
            maxDate,
            minDate,
            minuteStep,
            onChangeZonedAtJst,
            onHide,
            onShow,
            show,
            showTime,
            valueZonedAtJst,
        } = useContext(DateSelectContext);

        const dateFormat = `yyyy-MM-dd${showTime ? ', HH:mm' : ''}`;

        const valueText = useMemo(
            () => (valueZonedAtJst ? format(valueZonedAtJst, dateFormat) : ''),
            [dateFormat, valueZonedAtJst],
        );

        const [inputText, setInputText] = useState<string>(valueText);

        useEffect(() => {
            setInputText(valueText);
        }, [valueText]);

        const checkValidDate = useCallback(
            (date: Date) => {
                if (isValid(date) && isWithinRange(date, minDate, maxDate)) {
                    return showTime ? checkValidTime(date, minuteStep) : true;
                }

                return false;
            },
            [maxDate, minDate, minuteStep, showTime],
        );

        const onInputChange = useCallback(
            (event: ChangeEvent<HTMLInputElement>, resetIfInvalid = false) => {
                const newInputText = event.target.value;
                const newDateZonedAtJst = parse(
                    newInputText,
                    dateFormat,
                    new Date(),
                );

                if (
                    checkValidDate(newDateZonedAtJst) &&
                    newInputText === format(newDateZonedAtJst, dateFormat)
                ) {
                    setInputText(newInputText);
                    onChangeZonedAtJst(newDateZonedAtJst, true);
                } else if (newInputText === '') {
                    setInputText(newInputText);
                    onChangeZonedAtJst(undefined, true);
                } else {
                    setInputText(resetIfInvalid ? valueText : newInputText);
                }
            },
            [checkValidDate, dateFormat, onChangeZonedAtJst, valueText],
        );

        const onClick = useCallback(
            (event: MouseEvent<HTMLInputElement>) => {
                if (!show) {
                    event.preventDefault();
                    onShow();
                }
            },
            [onShow, show],
        );

        return (
            <div className={`relative ${showTime ? 'min-w-48' : 'min-w-36'}`}>
                <input
                    aria-controls={showTime ? 'datetime-select' : 'date-select'}
                    aria-expanded={show}
                    aria-label={showTime ? 'datetime-select' : 'date-select'}
                    className={twMerge(
                        dateSelectInput,
                        'select-none w-full border rounded text-base items-center placeholder-shown:overflow-ellipsis pr-8',
                        !show && 'cursor-pointer',
                        isCompact && 'h-8 text-sm',
                        className,
                    )}
                    data-datetime={true}
                    data-disabled={disabled}
                    data-dropdown-action={true}
                    data-invalid={!!error}
                    data-status={valueZonedAtJst ? 'selected' : undefined}
                    disabled={disabled}
                    onBlur={(event) => {
                        onInputChange(event, true);

                        if (show) {
                            onHide();
                        }
                    }}
                    onChange={onInputChange}
                    onClick={onClick}
                    onMouseDown={onClick}
                    placeholder={
                        placeholder ||
                        (showTime
                            ? c('component').t`Select date and time`
                            : c('component').t`Select date`)
                    }
                    ref={ref}
                    role="combobox"
                    type="text"
                    value={inputText}
                    {...rest}
                />
                <Icon
                    className={twMerge(
                        'absolute top-0 right-0 p-3 pr-4 pointer-events-none z-10',
                        disabled && 'text-light-disabled',
                        isCompact && '-top-1',
                    )}
                    icon="calendar"
                    size="base"
                />
            </div>
        );
    },
);

DateSelectInput.displayName = 'DateSelectInput';

export default DateSelectInput;
